import { motion } from "framer-motion";
import { Star, MapPin, Users, Award } from "lucide-react";
import { ABOUT_IMAGE, HOTEL } from "../data/site";

const stats = [
  { icon: Star, k: "4.1★", v: "Guest Rating" },
  { icon: Users, k: "385+", v: "Verified Reviews" },
  { icon: MapPin, k: "Bhadrak", v: "Central Location" },
  { icon: Award, k: "Trusted", v: "Family Run" },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="section-padding bg-background">
      <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* Image side */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden rounded-3xl shadow-2xl shadow-[#7A1A24]/15">
            <img
              src={ABOUT_IMAGE}
              alt="Maa Durga Palace & Guest House — illuminated night view of the property with the AC Kalyan Mandap signage, Bagurai, Bhadrak"
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A0608]/30 to-transparent" />
          </div>

          {/* Floating stat card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="absolute -bottom-6 -right-4 sm:-right-10 glass-card rounded-2xl p-5 w-60"
          >
            <div className="flex items-center gap-1 mb-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  size={14}
                  className={s <= 4 ? "fill-[#D4AF37] text-[#D4AF37]" : "fill-[#D4AF37]/30 text-[#D4AF37]"}
                />
              ))}
              <span className="ml-1.5 text-sm font-semibold text-foreground">{HOTEL.rating}</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Based on <strong className="text-foreground">{HOTEL.reviewCount}</strong> guest
              reviews — consistently rated for cleanliness & hospitality.
            </p>
          </motion.div>

          {/* Decorative gold ornament */}
          <div aria-hidden className="absolute -top-6 -left-6 h-24 w-24 rounded-full border border-[#D4AF37]/40 hidden sm:block" />
          <div aria-hidden className="absolute -top-2 -left-2 h-16 w-16 rounded-full bg-[#D4AF37]/10 blur-2xl" />
        </motion.div>

        {/* Text side */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <span className="eyebrow">About the Property</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-foreground">
            A property built for celebrations,<br />
            <span className="italic text-[#7A1A24] dark:text-[#D4AF37]">trusted by families.</span>
          </h2>
          <div className="mt-6 ornament-divider max-w-[200px]">
            <span className="text-lg">❖</span>
          </div>

          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Tucked into Bagurai, Bhadrak, <strong className="text-foreground">Maa Durga Palace</strong>
            {" "}is — first and foremost — a premium <strong className="text-foreground">Kalyana Mandap and wedding venue</strong>,
            with comfortable on-site rooms for the family travelling in for the function.
          </p>
          <p className="mt-4 text-base text-muted-foreground leading-relaxed">
            Whether you're hosting 500 guests at a wedding reception or staying a single
            night with family — the same warm, family-run hospitality wraps around every
            moment. It's not just service. It's a welcome.
          </p>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {stats.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.v}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.6 }}
                  className="p-5 rounded-2xl border border-border bg-card"
                  data-testid={`about-stat-${i}`}
                >
                  <Icon size={18} className="text-[#D4AF37]" />
                  <div className="mt-3 font-display text-2xl text-foreground">{s.k}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.v}</div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
